import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Eye, Image, Calendar, Upload } from 'lucide-react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';

const Banners = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',      // <-- change here
    linkUrl: '',       // <-- change here
    status: 'active',
    startDate: '',
    endDate: '',
    position: 'top',
  });
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const data = await adminService.getBanners();
      
      if (Array.isArray(data)) {
        setBanners(data);
        setError(null);
      } else {
        setBanners([]);
        setError('Invalid data format received from server');
      }
    } catch (err) {
      setBanners([]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const mockBanners = [
    {
      id: 1,
      title: 'Summer Sale Banner',
      description: 'Promote summer sale campaign',
      image_url: 'https://via.placeholder.com/800x200',
      link_url: '/campaigns/summer-sale',
      status: 'active',
      start_date: '2024-06-01',
      end_date: '2024-08-31',
      position: 'top',
      clicks: 1250,
      impressions: 15000,
    },
    {
      id: 2,
      title: 'Welcome Banner',
      description: 'Welcome new users to the platform',
      image_url: 'https://via.placeholder.com/800x200',
      link_url: '/welcome',
      status: 'active',
      start_date: '2024-01-01',
      end_date: '2024-12-31',
      position: 'sidebar',
      clicks: 890,
      impressions: 12000,
    },
  ];

  const displayBanners = banners.length > 0 ? banners : mockBanners;
  
  const filteredBanners = displayBanners.filter(banner => {
    const matchesSearch = (
      (banner.title && banner.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (banner.description && banner.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    const matchesStatus = selectedStatus === 'all' || banner.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare form data with proper date handling
      const submissionData = {
        title: formData.title,
        description: formData.description,
        imageUrl: formData.imageUrl,
        linkUrl: formData.linkUrl,
        status: formData.status,
        position: formData.position,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
      };

      if (editingBanner) {
        await adminService.updateBanner(editingBanner.id, submissionData);
        toast.success('Banner updated successfully!');
      } else {
        await adminService.createBanner(submissionData);
        toast.success('Banner created successfully!');
      }
      
      setShowForm(false);
      setEditingBanner(null);
      setFormData({
        title: '',
        description: '',
        imageUrl: '',
        linkUrl: '',
        status: 'active',
        startDate: '',
        endDate: '',
        position: 'top',
      });
      setImagePreview('');
      fetchBanners();
    } catch (error) {
      console.error('Banner submission error:', error);
      toast.error(editingBanner ? 'Failed to update banner' : 'Failed to create banner');
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      description: banner.description || '',
      imageUrl: banner.imageUrl || banner.image_url || '',
      linkUrl: banner.linkUrl || banner.link_url || '',
      status: banner.status || 'active',
      startDate: banner.startDate ? banner.startDate.split('T')[0] : '',
      endDate: banner.endDate ? banner.endDate.split('T')[0] : '',
      position: banner.position || 'top',
    });
    setImagePreview(banner.imageUrl || banner.image_url || '');
    setShowForm(true);
  };

  const handleDelete = async (bannerId) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await adminService.deleteBanner(bannerId);
        toast.success('Banner deleted successfully!');
        fetchBanners();
      } catch (error) {
        toast.error('Failed to delete banner');
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setLoading(true);
      const data = await adminService.uploadBannerImage(file);
      
      if (data.imageUrl) {
        setFormData((prev) => ({ ...prev, imageUrl: data.imageUrl }));
        setImagePreview(data.imageUrl);
        toast.success('Image uploaded successfully!');
      } else {
        toast.error('Image upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Image upload failed');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      expired: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config[status] || config.inactive}`}>
        {status || 'inactive'}
      </span>
    );
  };

  const getPositionBadge = (position) => {
    const config = {
      top: 'bg-blue-100 text-blue-800',
      sidebar: 'bg-purple-100 text-purple-800',
      footer: 'bg-orange-100 text-orange-800',
      popup: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config[position] || config.top}`}>
        {position || 'top'}
      </span>
    );
  };

  const positions = [
    { value: 'top', label: 'Top' },
    { value: 'sidebar', label: 'Sidebar' },
    { value: 'footer', label: 'Footer' },
    { value: 'popup', label: 'Popup' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Banner Management</h1>
          <p className="text-gray-600">Manage promotional banners and announcements</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Banner
        </button>
      </div>

      {showForm && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingBanner ? 'Edit Banner' : 'Add New Banner'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-field w-full"
                  placeholder="Enter banner title"
                  // required removed
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
                <select
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="input-field w-full"
                >
                  {positions.map(pos => (
                    <option key={pos.value} value={pos.value}>{pos.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="input-field w-full"
                placeholder="Enter banner description"
                // required removed
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="url"
                    value={formData.imageUrl}
                    onChange={(e) => {
                      setFormData({ ...formData, imageUrl: e.target.value });
                      setImagePreview(e.target.value);
                    }}
                    className="input-field w-full"
                    placeholder="Enter image URL or upload"
                  />
                  <label className={`btn-secondary cursor-pointer flex items-center gap-1 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <Upload className="h-4 w-4" />
                    {loading ? 'Uploading...' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                      disabled={loading}
                    />
                  </label>
                </div>
                {/* Image preview in form */}
                {imagePreview && (
                  <div className="mt-2 aspect-video w-full rounded border overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img
                      src={imagePreview}
                      alt="Banner Preview"
                      className="w-full h-full object-cover"
                      style={{ aspectRatio: '16/9' }}
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/400x225?text=No+Preview'; }}
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Link URL</label>
                <input
                  type="url"
                  value={formData.linkUrl}
                  onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                  className="input-field w-full"
                  placeholder="Enter link URL"
                  // required removed
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="input-field w-full"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="input-field w-full"
                  // required removed
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="input-field w-full"
                  // required removed
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn-primary">
                {editingBanner ? 'Update Banner' : 'Create Banner'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingBanner(null);
                  setImagePreview('');
                  setFormData({
                    title: '',
                    description: '',
                    imageUrl: '',
                    linkUrl: '',
                    status: 'active',
                    startDate: '',
                    endDate: '',
                    position: 'top',
                  });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search banners..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="scheduled">Scheduled</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading banners...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredBanners.map((banner) => (
            <div key={banner.id} className="card p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusBadge(banner.status)}
                    {getPositionBadge(banner.position)}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{banner.title}</h3>
                  <p className="text-gray-600 mb-3">{banner.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(banner)}
                    className="text-blue-600 hover:text-blue-900 p-1"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(banner.id)}
                    className="text-red-600 hover:text-red-900 p-1"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* Image preview in banner card */}
              <div className="mb-4 flex justify-center">
                <div className="aspect-video w-full max-w-[400px] rounded-lg border overflow-hidden bg-gray-100 flex items-center justify-center">
                  <img
                    src={banner.imageUrl || banner.image_url}
                    alt={banner.title}
                    className="w-full h-full object-cover"
                    style={{ aspectRatio: '16/9' }}
                    onError={(e) => { 
                      e.target.src = 'https://via.placeholder.com/400x225?text=No+Preview'; 
                    }}
                  />
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {banner.startDate ? new Date(banner.startDate).toLocaleDateString() : 'No start date'} - {banner.endDate ? new Date(banner.endDate).toLocaleDateString() : 'No end date'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  <span>{banner.impressions?.toLocaleString() || '0'} impressions</span>
                </div>
                <div className="flex items-center gap-2">
                  <Image className="h-4 w-4" />
                  <span>{banner.clicks?.toLocaleString() || '0'} clicks</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredBanners.length === 0 && (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <Image className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No banners found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedStatus !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'No banners have been created yet.'
            }
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create First Banner
          </button>
        </div>
      )}
    </div>
  );
};

export default Banners;
